/**
 * Calcul des analyses du restaurateur — fonction pure, sans Firestore.
 *
 * Reproduit les 6 vues de la fiche « Analyses » du site atable.fr :
 * chiffre d'affaires, rétention, points, affluence, clientèle, fréquence,
 * plus 4 indicateurs clés avec variation vs mois précédent.
 *
 * Tout est calculé à partir des visites validées, des commandes caisse, des
 * adhésions, des récompenses servies et des segments calculés la nuit.
 */
import type {
  AnalyticsModel, AnalyticsRaw, AnalyticsView, Kpi, MonthPoint, RawVisit, SegmentKey, SideStat,
} from './types';

const DAY_MS = 86_400_000;

export const fmtEuro = (n: number, digits = 0) =>
  `${n.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} €`;
export const fmtPct = (n: number, digits = 1) =>
  `${n.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`;
export const fmtInt = (n: number) => n.toLocaleString('fr-FR');

const monthStart = (d: Date, offset = 0) => new Date(d.getFullYear(), d.getMonth() + offset, 1);
const monthLabel = (d: Date) => {
  const s = d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
  return s.charAt(0).toUpperCase() + s.slice(1) + (s.length > 4 ? '.' : '');
};
const sameMonth = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const inRange = (d: Date | null, from: Date, to: Date) => !!d && d >= from && d < to;

/** Les 6 derniers mois, du plus ancien au courant. */
function lastMonths(now: Date, count = 6): Date[] {
  return Array.from({ length: count }, (_, i) => monthStart(now, i - (count - 1)));
}

function pctDelta(cur: number, prev: number): { text: string; positive: boolean } | null {
  if (!prev) return null;
  const d = ((cur - prev) / prev) * 100;
  return { text: `${d >= 0 ? '+' : '−'}${Math.abs(d).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} %`, positive: d >= 0 };
}

function ptsDelta(cur: number, prev: number | null): { text: string; positive: boolean } | null {
  if (prev === null) return null;
  const d = cur - prev;
  return { text: `${d >= 0 ? '+' : '−'}${Math.abs(d).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} pts`, positive: d >= 0 };
}

function visitAmountEuros(v: RawVisit): number {
  return (Number(v.amount) || 0) / 100;
}

/** Visites ayant une date, triées. */
function datedVisits(visits: RawVisit[]): Array<RawVisit & { createdAt: Date }> {
  return visits
    .filter((v): v is RawVisit & { createdAt: Date } => v.createdAt instanceof Date && !isNaN(v.createdAt.getTime()))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/** Libellé court d'un mois : « sept. », « oct. ». */
const monthShort = (d: Date) => d.toLocaleDateString('fr-FR', { month: 'short' });

export function computeAnalytics(raw: AnalyticsRaw, now: Date = new Date()): AnalyticsModel {
  const visits = datedVisits(raw.visits);
  const months = lastMonths(now);
  const thisMonth = monthStart(now);
  const nextMonth = monthStart(now, 1);
  const prevMonth = monthStart(now, -1);
  // Comparaison à période égale : du 1er au jour N du mois en cours, contre du 1er au
  // jour N du mois précédent (borné à sa fin) — pas un mois entamé contre un mois entier.
  const elapsedMs = now.getTime() - thisMonth.getTime();
  const prevPeriodEnd = new Date(Math.min(prevMonth.getTime() + elapsedMs, thisMonth.getTime()));
  const dayN = now.getDate();
  const comparisonLabel = dayN === 1 ? `le 1er ${monthShort(prevMonth)}` : `1–${Math.min(dayN, new Date(now.getFullYear(), now.getMonth(), 0).getDate())} ${monthShort(prevMonth)}`;
  const stampsProgram = raw.programType === 'stamps';
  const memberIds = new Set(raw.memberships.map((m) => m.userId));
  const memberCount = memberIds.size;

  const visitsByUser = new Map<string, Date[]>();
  for (const v of visits) {
    if (!v.userId) continue;
    const arr = visitsByUser.get(v.userId) ?? [];
    arr.push(v.createdAt);
    visitsByUser.set(v.userId, arr);
  }

  /* ── Chiffre d'affaires fidélité par mois ─────────────────────────────── */
  const revenueByMonth: MonthPoint[] = months.map((m) => ({
    label: monthLabel(m),
    value: Math.round(visits.filter((v) => sameMonth(v.createdAt, m)).reduce((s, v) => s + visitAmountEuros(v), 0)),
  }));
  const revenueTotal = revenueByMonth.reduce((s, p) => s + (p.value ?? 0), 0);
  const firstNonZero = revenueByMonth.find((p) => (p.value ?? 0) > 0)?.value ?? 0;
  const revenueProgress = pctDelta(revenueByMonth[revenueByMonth.length - 1].value ?? 0, firstNonZero);

  const memberVisitsWithAmount = visits.filter((v) => (Number(v.amount) || 0) > 0);
  const avgTicketMember = memberVisitsWithAmount.length
    ? memberVisitsWithAmount.reduce((s, v) => s + visitAmountEuros(v), 0) / memberVisitsWithAmount.length
    : 0;
  const paidOrders = raw.orders.filter((o) => o.status === 'paid' && (Number(o.total) || 0) > 0);
  const nonMemberOrders = paidOrders.filter((o) => !o.customerId || !memberIds.has(o.customerId));
  const avgTicketOther = nonMemberOrders.length
    ? nonMemberOrders.reduce((s, o) => s + (Number(o.total) || 0), 0) / nonMemberOrders.length
    : null;

  const revenue: AnalyticsView = {
    kind: 'line',
    unit: 'euro',
    points: revenueByMonth,
    stats: [
      { label: 'Total sur 6 mois', value: fmtEuro(revenueTotal) },
      { label: 'Progression', value: revenueProgress ? revenueProgress.text : '—', hint: `depuis ${revenueByMonth[0].label.toLowerCase()}` },
      {
        label: 'Panier fidèle vs autres',
        value: avgTicketOther !== null ? `${fmtEuro(avgTicketMember, 2)} / ${fmtEuro(avgTicketOther, 2)}` : fmtEuro(avgTicketMember, 2),
        hint: avgTicketOther === null ? 'pas encore de comparaison caisse' : undefined,
      },
    ],
    caption: "Chiffre d'affaires réalisé avec les membres du programme, mois par mois.",
  };

  /* ── Rétention : part des visites suivies d'un retour sous 30 jours ──── */
  const returnRateForMonth = (m: Date): number | null => {
    const mEnd = monthStart(m, 1);
    let total = 0;
    let returned = 0;
    for (const [, dates] of visitsByUser) {
      for (let i = 0; i < dates.length; i++) {
        const d = dates[i];
        if (!inRange(d, m, mEnd)) continue;
        // on ne juge que les visites qui ont eu 30 jours pour être suivies
        if (now.getTime() - d.getTime() < 30 * DAY_MS) continue;
        total++;
        const next = dates.slice(i + 1).find((x) => x > d);
        if (next && next.getTime() - d.getTime() <= 30 * DAY_MS) returned++;
      }
    }
    return total ? (returned / total) * 100 : null;
  };
  const retentionByMonth: MonthPoint[] = months.map((m) => {
    const r = returnRateForMonth(m);
    return { label: monthLabel(m), value: r === null ? null : Math.round(r * 10) / 10 };
  });
  const lastKnownRetention = [...months].reverse().map(returnRateForMonth).find((r) => r !== null) ?? null;

  const atRisk = raw.memberships.filter((m) => {
    const last = m.lastVisitAt ?? visitsByUser.get(m.userId)?.slice(-1)[0] ?? null;
    return last && now.getTime() - last.getTime() > 35 * DAY_MS;
  }).length;

  const gaps: number[] = [];
  for (const [, dates] of visitsByUser) {
    for (let i = 1; i < dates.length; i++) gaps.push((dates[i].getTime() - dates[i - 1].getTime()) / DAY_MS);
  }
  const avgGap = gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : null;

  const retention: AnalyticsView = {
    kind: 'line',
    unit: 'percent',
    points: retentionByMonth,
    stats: [
      { label: 'Reviennent sous 30 jours', value: lastKnownRetention !== null ? fmtPct(lastKnownRetention) : '—' },
      { label: 'Clients à risque', value: fmtInt(atRisk), hint: 'pas venus depuis 5 semaines' },
      { label: 'Délai moyen entre 2 visites', value: avgGap !== null ? `${Math.round(avgGap)} jours` : '—' },
    ],
    caption: 'Part des membres qui reviennent dans les 30 jours suivant leur visite.',
  };

  /* ── Points (ou tampons) gagnés / utilisés ──────────────────────────────── */
  // Unité du programme : un programme à tampons crédite stampsEarned et memberships.stamps.
  const Unit = stampsProgram ? 'Tampons' : 'Points';
  const confirmed = raw.redemptions.filter((r) => r.status === 'confirmed' && r.confirmedAt);
  const earnedOf = (v: RawVisit) => Number(stampsProgram ? v.stampsEarned : v.pointsEarned) || 0;
  const earnedByMonth = months.map((m) => visits.filter((v) => sameMonth(v.createdAt, m)).reduce((s, v) => s + earnedOf(v), 0));
  const usedByMonth = months.map((m) => confirmed.filter((r) => sameMonth(r.confirmedAt as Date, m)).reduce((s, r) => s + (Number(r.cost) || 0), 0));
  const lastEarned = earnedByMonth[earnedByMonth.length - 1];
  const lastUsed = usedByMonth[usedByMonth.length - 1];
  const inCirculation = raw.memberships.reduce((s, m) => s + (Number(stampsProgram ? m.stamps : m.points) || 0), 0);
  const rewardsThisMonth = confirmed.filter((r) => inRange(r.confirmedAt, thisMonth, nextMonth)).length;

  const points: AnalyticsView = {
    kind: 'groupedBars',
    labels: months.map(monthLabel),
    a: { name: `${Unit} gagnés`, values: earnedByMonth },
    b: { name: `${Unit} utilisés`, values: usedByMonth },
    stats: [
      { label: "Taux d'utilisation", value: lastEarned ? fmtPct((lastUsed / lastEarned) * 100) : '—', hint: `en ${monthLabel(thisMonth).toLowerCase()}` },
      { label: `${Unit} en circulation`, value: fmtInt(inCirculation) },
      { label: 'Récompenses servies', value: fmtInt(rewardsThisMonth), hint: 'ce mois-ci' },
    ],
    caption: `${Unit} gagnés et utilisés par vos clients chaque mois.`,
  };

  /* ── Affluence : couverts moyens par service, 4 dernières semaines ─────── */
  const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const fourWeeksAgo = new Date(now.getTime() - 28 * DAY_MS);
  const recent = visits.filter((v) => v.createdAt >= fourWeeksAgo);
  const service = (d: Date): 'midi' | 'soir' | null => {
    const h = d.getHours();
    if (h >= 11 && h < 16) return 'midi';
    if (h >= 18 || h < 2) return 'soir';
    return null;
  };
  const grid = { midi: Array(7).fill(0) as number[], soir: Array(7).fill(0) as number[] };
  for (const v of recent) {
    const s = service(v.createdAt);
    if (!s) continue;
    const dow = (v.createdAt.getDay() + 6) % 7; // lundi = 0
    grid[s][dow] += 1;
  }
  // Moyenne par semaine avec une décimale (0,3 visite ≠ 0) ; on compte des visites validées,
  // pas des couverts (une visite = un client, quelle que soit la taille de la table).
  const avg = (arr: number[]) => arr.map((n) => Math.round((n / 4) * 10) / 10);
  const midi = avg(grid.midi);
  const soir = avg(grid.soir);
  const cells = [...midi.map((v, i) => ({ v, label: `${DAYS[i].toLowerCase()} midi` })), ...soir.map((v, i) => ({ v, label: `${DAYS[i].toLowerCase()} soir` }))];
  const active = cells.filter((c) => c.v > 0);
  const hasTraffic = active.length > 0;
  const fmtVisits = (n: number) => `${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} visite${n >= 2 ? 's' : ''}`;
  const peak = hasTraffic ? active.reduce((a, b) => (b.v > a.v ? b : a)) : null;
  // « Moment calme » : parmi les services où il se passe quelque chose (un service fermé
  // n'est pas un creux à remplir).
  const calm = active.length > 1 ? active.reduce((a, b) => (b.v < a.v ? b : a)) : null;

  const traffic: AnalyticsView = {
    kind: 'heatmap',
    days: DAYS,
    rows: [{ name: 'Midi', values: midi }, { name: 'Soir', values: soir }],
    stats: peak
      ? [
        { label: 'Pic de la semaine', value: fmtVisits(peak.v), hint: peak.label },
        ...(calm ? [
          { label: 'Moment le plus calme', value: calm.label, hint: `${fmtVisits(calm.v)} en moyenne` },
          { label: 'Idée', value: 'Vente flash', hint: `le ${calm.label}` },
        ] : []),
      ]
      : [{ label: 'Pas encore de visite sur 4 semaines', value: '—' }],
    caption: 'Visites validées en moyenne par service et par semaine, sur les 4 dernières semaines.',
  };

  /* ── Clientèle : répartition par segment ──────────────────────────────── */
  const SEG_LABEL: Record<SegmentKey, string> = { nouveau: 'Nouveaux', habitue: 'Habitués', vip: 'VIP', gros_panier: 'Gros paniers', a_risque: 'À risque' };
  const segByUser = new Map(raw.segments.map((s) => [s.userId, s.segment]));
  const counts: Record<SegmentKey | 'autres', number> = { nouveau: 0, habitue: 0, vip: 0, gros_panier: 0, a_risque: 0, autres: 0 };
  for (const m of raw.memberships) {
    const seg = segByUser.get(m.userId) ?? null;
    if (seg) counts[seg] += 1; else counts.autres += 1;
  }
  const donutSegments = (Object.keys(SEG_LABEL) as SegmentKey[])
    .map((k) => ({ name: SEG_LABEL[k], value: counts[k] }))
    .concat(counts.autres ? [{ name: 'Sans segment', value: counts.autres }] : [])
    .filter((s) => s.value > 0);
  const ninetyDaysAgo = new Date(now.getTime() - 90 * DAY_MS);
  const activeMembers = raw.memberships.filter((m) => {
    const last = m.lastVisitAt ?? visitsByUser.get(m.userId)?.slice(-1)[0] ?? null;
    return last && last >= ninetyDaysAgo;
  }).length;
  const newThisMonth = raw.memberships.filter((m) => inRange(m.joinedDate, thisMonth, nextMonth)).length;
  const customers: AnalyticsView = {
    kind: 'donut',
    segments: donutSegments,
    total: memberCount,
    totalLabel: 'membres',
    stats: [
      { label: 'Membres actifs', value: fmtInt(activeMembers), hint: 'venus dans les 90 jours' },
      { label: 'Nouveaux ce mois', value: `+${fmtInt(newThisMonth)}` },
      { label: 'VIP', value: fmtInt(counts.vip), hint: memberCount ? `${fmtPct((counts.vip / memberCount) * 100)} des membres` : undefined },
    ],
    caption: 'Répartition de vos membres par segment (calculé chaque nuit).',
  };

  /* ── Fréquence : visites par membre sur 30 jours ─────────────────────── */
  const thirtyDaysAgo = new Date(now.getTime() - 30 * DAY_MS);
  const perUser30 = new Map<string, number>();
  for (const v of visits) {
    if (!v.userId || v.createdAt < thirtyDaysAgo) continue;
    perUser30.set(v.userId, (perUser30.get(v.userId) ?? 0) + 1);
  }
  const buckets = [0, 0, 0, 0, 0]; // 1, 2, 3, 4, 5+
  for (const n of perUser30.values()) buckets[Math.min(n, 5) - 1] += 1;
  const visitors30 = perUser30.size;
  const pct = (n: number) => (visitors30 ? Math.round((n / visitors30) * 1000) / 10 : 0);
  const totalVisits30 = [...perUser30.values()].reduce((s, n) => s + n, 0);
  const frequency: AnalyticsView = {
    kind: 'hbars',
    rows: ['1 visite', '2 visites', '3 visites', '4 visites', '5 et +'].map((name, i) => ({ name, value: pct(buckets[i]) })),
    stats: [
      { label: 'Visites par membre', value: visitors30 ? (totalVisits30 / visitors30).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : '—', hint: 'par mois en moyenne' },
      { label: 'Viennent 3 fois et +', value: fmtPct(pct(buckets[2] + buckets[3] + buckets[4])) },
      { label: 'Ont fait 1 seule visite', value: fmtPct(pct(buckets[0])), hint: 'à relancer en priorité' },
    ],
    caption: 'Nombre de visites par membre sur les 30 derniers jours.',
  };

  /* ── KPI ──────────────────────────────────────────────────────────────── */
  const visitsCount = (m: Date) => visits.filter((v) => sameMonth(v.createdAt, m)).length;
  const loyaltyRateAt = (until: Date): number | null => {
    // membres (inscrits avant `until`) ayant au moins 2 visites avant `until`
    const members = raw.memberships.filter((m) => !m.joinedDate || m.joinedDate < until);
    if (!members.length) return null;
    const returning = members.filter((m) => (visitsByUser.get(m.userId) ?? []).filter((d) => d < until).length >= 2).length;
    return (returning / members.length) * 100;
  };
  const loyaltyNow = loyaltyRateAt(nextMonth);
  const loyaltyPrev = loyaltyRateAt(thisMonth);
  const avgTicketIn = (from: Date, to: Date) => {
    const vs = visits.filter((v) => inRange(v.createdAt, from, to) && (Number(v.amount) || 0) > 0);
    return vs.length ? vs.reduce((s, v) => s + visitAmountEuros(v), 0) / vs.length : 0;
  };
  const avgTicketFor = (m: Date) => avgTicketIn(m, monthStart(m, 1));
  const visitsIn = (from: Date, to: Date) => visits.filter((v) => inRange(v.createdAt, from, to)).length;
  const revenueIn = (from: Date, to: Date) =>
    Math.round(visits.filter((v) => inRange(v.createdAt, from, to)).reduce((s, v) => s + visitAmountEuros(v), 0));

  // Mois en cours (jusqu'à maintenant) contre la même période du mois précédent.
  const ticketNow = avgTicketIn(thisMonth, nextMonth);
  const ticketPrev = avgTicketIn(prevMonth, prevPeriodEnd);
  const ticketDelta = ticketPrev ? { text: `${ticketNow - ticketPrev >= 0 ? '+' : '−'}${fmtEuro(Math.abs(ticketNow - ticketPrev), 2)}`, positive: ticketNow >= ticketPrev } : null;
  const visitsNow = visitsIn(thisMonth, nextMonth);
  const visitsPrev = visitsIn(prevMonth, prevPeriodEnd);
  const visitsDelta = pctDelta(visitsNow, visitsPrev);
  const revNow = revenueByMonth[revenueByMonth.length - 1].value ?? 0;
  const revPrev = revenueIn(prevMonth, prevPeriodEnd);
  const revDelta = pctDelta(revNow, revPrev);
  const loyDelta = loyaltyNow !== null ? ptsDelta(loyaltyNow, loyaltyPrev) : null;
  const sparkOf = (fn: (m: Date) => number) => months.map(fn);

  const kpis: Kpi[] = [
    {
      label: 'Taux de fidélisation',
      value: loyaltyNow !== null ? fmtPct(loyaltyNow) : '—',
      delta: loyDelta?.text ?? null, deltaPositive: loyDelta?.positive ?? null,
      spark: sparkOf((m) => loyaltyRateAt(monthStart(m, 1)) ?? 0),
    },
    {
      label: 'Panier moyen',
      value: fmtEuro(ticketNow, 2),
      delta: ticketDelta?.text ?? null, deltaPositive: ticketDelta?.positive ?? null,
      spark: sparkOf(avgTicketFor),
    },
    {
      label: 'Visites ce mois',
      value: fmtInt(visitsNow),
      delta: visitsDelta?.text ?? null, deltaPositive: visitsDelta?.positive ?? null,
      spark: sparkOf(visitsCount),
    },
    {
      label: 'CA lié à la fidélité',
      value: fmtEuro(revNow),
      delta: revDelta?.text ?? null, deltaPositive: revDelta?.positive ?? null,
      spark: revenueByMonth.map((p) => p.value ?? 0),
    },
  ];

  return {
    kpis,
    views: { revenue, retention, points, traffic, customers, frequency },
    empty: visits.length === 0,
    memberCount,
    comparisonLabel,
    computedAt: now,
  };
}
